// Pack summaries are now owned by the single reconciler in grg-club-cart.js
// (window.GRGClub.reconcileGrgAttributes). Each pack line carries its
// _pack_summary as a hidden line-item property; the reconciler rebuilds the
// `_grg_pack_summaries` cart attribute from those on every cart mutation.
// The former fetch interceptor + append-persist writer lived here — both
// have been removed to prevent stale-attr bleed at checkout.

(() => {
  const portalEl = document.querySelector('.grg-portal');
  const CLUB_TAG = (portalEl && portalEl.dataset.clubTag) || window.__grgClubTag || '';

  // ── Numeric sizes ascending sort ─────────────────────────────────────
  // Shopify returns variants in the order they were entered in admin —
  // clients often add sizes largest first. Business rule is smallest
  // first. This sorts every option whose visible label STARTS with a
  // digit followed by a dash or inch mark, ascending by leading number.
  // Covers:
  //   • kids numeric ranges   "1-2, 3-4, 5-6, 7-8, 9-10, 11-12"
  //   • kids inches           "16\"(1/2), 18\"(3/4)…"
  //   • adults inches         "28\"(13/14), 30\"(S), 32\"(M)…"
  // Skipped (kept in admin order):
  //   • letter sizes          XS, S, M, L, XL, XXL, 3XL, 4XL, 5XL
  //   • "Please Select" placeholder
  // Idempotent — safe to run multiple times.
  function sortNumericSizesAscending(sizeSelect) {
    if (!sizeSelect || sizeSelect.tagName !== 'SELECT') return;
    const allOptions = Array.from(sizeSelect.options);

    // Sort key MUST come from the visible label (textContent), not `value`
    // — on single-mode selects the option value is Shopify's variant ID
    // (e.g. 43824923940) which would collapse the sort to ID order and
    // ignore the actual size number. Match `^\d+["-]` to include both
    // ranges ("5-6") and inches ("26\"(11/12)") while excluding letter
    // sizes like 3XL, 4XL, 5XL (digit followed by a letter, not " or -).
    const NUMERIC_SIZE_RE = /^(\d+)["\-]/;
    const isNumeric = (opt) => NUMERIC_SIZE_RE.test(String(opt.textContent).trim());
    const numKey = (opt) => {
      const m = String(opt.textContent).trim().match(NUMERIC_SIZE_RE);
      return m ? parseInt(m[1], 10) : 9999;
    };

    const numericOptions = allOptions.filter(isNumeric);
    if (numericOptions.length < 2) return;

    const sorted = [...numericOptions].sort((a, b) => numKey(a) - numKey(b));

    // Bail if already sorted — avoids DOM churn on repeat calls.
    const already = numericOptions.every((opt, i) => opt === sorted[i]);
    if (already) return;

    // Rebuild the select preserving non-numeric options in their original
    // positions; numeric slots get filled from the sorted list in order.
    const numericIndexSet = new Set(numericOptions.map((o) => allOptions.indexOf(o)));
    let sortedIdx = 0;
    const newOrder = allOptions.map((opt, idx) => (numericIndexSet.has(idx) ? sorted[sortedIdx++] : opt));

    // Remove & re-append — <select> keeps selection state by value, not by
    // reference position, so the currently-selected option (if any) stays
    // selected after re-ordering.
    while (sizeSelect.firstChild) sizeSelect.removeChild(sizeSelect.firstChild);
    newOrder.forEach((opt) => sizeSelect.appendChild(opt));
  }

  // ── Age filter for size dropdowns ────────────────────────────────────
  // iPhone browsers (Safari AND Chrome — both run on WebKit) ignore `hidden`
  // on <option> inside the native picker, so Kids packs listed adult sizes
  // there. Non-matching options are physically removed instead. The full
  // list is cached once (after the numeric sort above) and re-appended in
  // that order on every age change. age '' restores every option.
  function filterSizesByAge(sizeSelect, age) {
    if (!sizeSelect || sizeSelect.tagName !== 'SELECT') return;
    if (!sizeSelect._grgAgeOptions) {
      sizeSelect._grgAgeOptions = Array.from(sizeSelect.querySelectorAll('option[data-age]'));
    }
    sizeSelect._grgAgeOptions.forEach((opt) => opt.remove());
    sizeSelect._grgAgeOptions.forEach((opt) => {
      opt.hidden = false;
      if (age === '' || opt.dataset.age === age) sizeSelect.appendChild(opt);
    });
  }

  // Message when a selected size doesn't belong to the chosen age group.
  function wrongAgeMessage(age) {
    return age === 'kids'
      ? 'This is a Kids order. Please choose kids sizes only.'
      : 'This is an Adults order. Please choose adult sizes only.';
  }

  function sortAllSizes(root) {
    (root || document).querySelectorAll(
      '.grg-bundle-component-size, .grg-size-select'
    ).forEach(sortNumericSizesAscending);
  }

  // Run once at load for all currently-mounted size selects.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sortAllSizes(document));
  } else {
    sortAllSizes(document);
  }

  // Open modals — buttons AND the whole pack card
  document.querySelectorAll('[data-open-modal]').forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      // If a nested button was clicked, let its own listener handle it (avoid double open)
      if (trigger.tagName !== 'BUTTON' && e.target.closest('button[data-open-modal]')) return;
      const modal = document.getElementById(trigger.dataset.openModal);
      if (modal) modal.showModal();
    });
    // Keyboard support for div-based cards
    if (trigger.tagName !== 'BUTTON') {
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const modal = document.getElementById(trigger.dataset.openModal);
          if (modal) modal.showModal();
        }
      });
    }
  });

  // Close on X button
  document.querySelectorAll('[data-close-modal]').forEach((btn) => {
    btn.addEventListener('click', () => btn.closest('dialog')?.close());
  });

  // Close on backdrop click
  document.querySelectorAll('.grg-pack-modal').forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.close();
    });
  });

  // Age group → gate + filter size options (single mode). Age Group is
  // MANDATORY before Size: the size dropdown stays disabled (with an
  // explanatory placeholder) until an age is chosen, then filters to that age.
  document.querySelectorAll('.grg-age-select').forEach((select) => {
    const modal = select.closest('.grg-pack-modal');
    const sizeSelect = modal.querySelector('.grg-size-select');

    const syncSingleSize = () => {
      if (!sizeSelect) return; // single-variant product: no size dropdown
      const age = select.value;
      sizeSelect.disabled = !age;

      // Placeholder reflects the age-first requirement.
      const placeholder = sizeSelect.querySelector('option[value=""]');
      if (placeholder) {
        placeholder.textContent = age ? 'Please Select' : 'Please select age group first';
      }

      filterSizesByAge(sizeSelect, age);
      sizeSelect.value = '';
    };

    select.addEventListener('change', syncSingleSize);
    // Enforce the locked state on load (no age picked yet).
    syncSingleSize();
  });

  // Age group → filter size options across all bundle components (bundle mode).
  // Age Group is MANDATORY before Size: every component's size dropdown stays
  // disabled until an age is chosen, and re-locks if the age is cleared.
  document.querySelectorAll('.grg-bundle-age-select').forEach((select) => {
    const modal = select.closest('.grg-pack-modal');

    const syncBundleSizes = () => {
      const age = select.value;

      modal.querySelectorAll('.grg-bundle-component-size').forEach((sizeSelect) => {
        // Single-variant components render as <input type="hidden">, not <select>.
        // They have no options to filter and no user-selected value to reset —
        // skip them, otherwise we'd blank the pre-filled "One Size" value and
        // fail validation on Add.
        if (sizeSelect.tagName !== 'SELECT') return;
        sizeSelect.disabled = !age;
        // Placeholder reflects the age-first requirement.
        const placeholder = sizeSelect.querySelector('option[value=""]');
        if (placeholder) {
          placeholder.textContent = age ? 'Please Select' : 'Please select age group first';
        }
        filterSizesByAge(sizeSelect, age);
        sizeSelect.value = '';
      });
    };

    select.addEventListener('change', syncBundleSizes);
    // Enforce the locked state on load (no age picked yet).
    syncBundleSizes();
  });

  // Add to Cart
  document.querySelectorAll('.grg-pack-modal__add-btn').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const modal = btn.closest('.grg-pack-modal');
      const errorEl = modal.querySelector('.grg-pack-modal__error');
      const feeVariantId = modal.dataset.feeVariantId;
      const isBundle = modal.dataset.mode === 'bundle';

      hideError(errorEl);
      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        const items = [];
        const feeCount = [];

        if (isBundle) {
          // Bundle mode — per-component size selectors, classify kids/adults, add as single pack line item.
          const componentSelects = modal.querySelectorAll('.grg-bundle-component-size');
          let valid = true;
          let hasKid = false;
          let hasAdult = false;
          const properties = {};

          // Age Group is mandatory before any size can be picked.
          const bundleAgeSelect = modal.querySelector('.grg-bundle-age-select');
          if (bundleAgeSelect && !bundleAgeSelect.value) {
            showError(errorEl, 'Please select an age group first.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          componentSelects.forEach((select) => {
            if (!select.value) { valid = false; return; }
            const componentTitle = select.dataset.componentTitle || `Item`;
            const sizeLabel = select.value;
            properties[componentTitle] = sizeLabel;

            // Trust the server's data-age classification on the selected option
            // over any regex on the label — inches sizes ('30"(S)') start with a
            // digit but ARE adult. Single-variant components render as a hidden
            // <input> (no .options); they carry no age and MUST NOT contribute
            // to the mix-check, otherwise a kids-only pack + a sizeless beanie
            // would falsely trip the "mixed sizes" error.
            if (select.tagName === 'SELECT') {
              const selectedOpt = select.options[select.selectedIndex];
              const optAge = selectedOpt && selectedOpt.dataset && selectedOpt.dataset.age;
              if (optAge === 'kids') hasKid = true;
              else if (optAge === 'adults') hasAdult = true;
            }
          });

          if (!valid) {
            showError(errorEl, 'Please select a size for every item in the bundle.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          // Final guard: every size must match the pack's Age Group.
          const bundleAge = bundleAgeSelect && bundleAgeSelect.value;
          if ((bundleAge === 'kids' && hasAdult) || (bundleAge === 'adults' && hasKid)) {
            showError(errorEl, wrongAgeMessage(bundleAge));
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          if (hasKid && hasAdult) {
            showError(
              errorEl,
              "Please choose all kid sizes or all adult sizes — mixing isn't supported on a pack. Buy the components individually if you need mixed sizes."
            );
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          // Confirmation checkbox (only present if any component allows initials)
          const confirmCheckbox = modal.querySelector('.grg-bundle-confirm');
          const initialsInputs = modal.querySelectorAll('.grg-initials-input');
          const hasInitialsValue = Array.from(initialsInputs).some((i) => i.value.trim());
          if (confirmCheckbox && hasInitialsValue && !confirmCheckbox.checked) {
            showError(errorEl, modal.dataset.initialsError || 'Please tick the box to confirm your initials are correct.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          // Add initials as line properties
          initialsInputs.forEach((input) => {
            const idx = input.dataset.bundleComponentIndex;
            const val = input.value.trim();
            if (val) {
              properties[`Initials ${idx}`] = val;
              feeCount.push(1);
            }
          });

          // Fetch LIVE variant data (the embedded JSON can be stale due to
          // Shopify full-page caching — variant IDs change if variants are
          // recreated). The .js endpoint is never cached.
          const targetOption = hasKid ? 'Kids' : 'Adults';
          let variants = null;
          const handle = modal.dataset.productHandle;
          if (handle) {
            try {
              const liveRes = await fetch('/products/' + handle + '.js', { credentials: 'same-origin' });
              if (liveRes.ok) {
                const liveProduct = await liveRes.json();
                variants = liveProduct.variants;
              }
            } catch (e) {
              /* fall back to embedded data below */
            }
          }
          if (!variants) {
            const variantsScript = modal.querySelector('script[data-bundle-variants-for]');
            if (!variantsScript) throw new Error('Bundle data missing.');
            variants = JSON.parse(variantsScript.textContent);
          }

          const matched =
            variants.find((v) => (v.options || []).some((o) => o === targetOption)) ||
            variants.find((v) => v.option1 === targetOption) ||
            variants.find((v) => v.available !== false) ||
            variants[0];

          if (!matched) throw new Error('Pack variant not found.');

          // Build the summary FIRST and attach it as a hidden line-item
          // property (`_pack_summary`). The reconciler in grg-club-cart.js
          // rebuilds the `_grg_pack_summaries` cart attribute from these on
          // every mutation — removing the pack line automatically drops its
          // summary from the attribute.
          const packSummary = buildPackSummary(
            modal.querySelector('.grg-pack-modal__title')?.textContent?.trim() || 'Pack',
            hasKid ? 'Kids' : 'Adults',
            properties
          );
          properties._pack_summary = packSummary;

          items.push({ id: matched.id, quantity: 1, properties });

          // Kept for the cart-note append below (customer-visible summary).
          btn._noteSummary = packSummary;

        } else {
          // Single mode — original behaviour
          const sizeSelect = modal.querySelector('.grg-size-select');
          const initialsInput = modal.querySelector('.grg-initials-input');

          // Age Group is mandatory before any size can be picked.
          const singleAgeSelect = modal.querySelector('.grg-age-select');
          if (singleAgeSelect && !singleAgeSelect.value) {
            showError(errorEl, 'Please select an age group first.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          if (sizeSelect && !sizeSelect.value) {
            showError(errorEl, 'Please select a size.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          // Final guard: the size must match the chosen Age Group.
          if (singleAgeSelect && singleAgeSelect.value && sizeSelect && sizeSelect.tagName === 'SELECT') {
            const pickedAge = sizeSelect.options[sizeSelect.selectedIndex]?.dataset?.age;
            if (pickedAge && pickedAge !== singleAgeSelect.value) {
              showError(errorEl, wrongAgeMessage(singleAgeSelect.value));
              btn.disabled = false;
              btn.textContent = 'Add to Cart';
              return;
            }
          }

          const initialsValue = initialsInput?.value.trim();

          // Require the "initials appear exactly" acknowledgement when the
          // customer has actually typed initials (checkbox only rendered on
          // products that offer initials).
          const confirmCheckbox = modal.querySelector('.grg-single-confirm');
          if (confirmCheckbox && initialsValue && !confirmCheckbox.checked) {
            showError(errorEl, modal.dataset.initialsError || 'Please tick the box to confirm your initials are correct.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            return;
          }

          const properties = {};
          if (initialsValue) {
            properties['Initials'] = initialsValue;
            feeCount.push(1);
          }

          // Pick the variant to add:
          //   - a size was selected → use it
          //   - no size selector rendered (product has only its default variant,
          //     e.g. Bobble Hat, Snood) → use the modal's data-default-variant-id
          let variantId = null;
          if (sizeSelect?.value) {
            variantId = parseInt(sizeSelect.value, 10);
          } else if (!sizeSelect && modal.dataset.defaultVariantId) {
            variantId = parseInt(modal.dataset.defaultVariantId, 10);
          }
          if (variantId) {
            items.push({ id: variantId, quantity: 1, properties });
          }
        }

        if (!items.length) throw new Error('No items to add.');

        // Combine the pack/item line + one initials-fee line per personalised
        // item into one /cart/add.js call. Each line carries `_club` so Shopify
        // Flow can tag the order by club. Cross-club mixing is no longer
        // blocked — the Fulfillment Picker handles pickup/delivery per club.
        const feeItems =
          feeVariantId && feeCount.length > 0
            ? feeCount.map(() => ({ id: parseInt(feeVariantId, 10), quantity: 1 }))
            : [];

        await window.GRGClub.addItems(items.concat(feeItems), CLUB_TAG);
        if (window.GRGClub.flashAdded) window.GRGClub.flashAdded(btn);

        // Cart note is customer-facing; still append the summary there for
        // in-cart visibility. The `_grg_pack_summaries` cart attribute is
        // rebuilt by the reconciler from each pack line's `_pack_summary`.
        if (btn._noteSummary) {
          await appendCartNote(btn._noteSummary);
          delete btn._noteSummary;
        }
        if (window.GRGClub && window.GRGClub.scheduleReconcile) {
          window.GRGClub.scheduleReconcile();
        }

        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'grg-portal' } }));

        modal.close();
        resetModal(modal);
      } catch (err) {
        showError(errorEl, err.message);
      } finally {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      }
    });
  });

  // ---- Club socks: guarded add ----
  document.querySelectorAll('.grg-sock-card__add').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const card = btn.closest('.grg-sock-card');
      const sizeSelect = card.querySelector('.grg-sock-card__size');
      const errorEl = card.querySelector('.grg-sock-card__error');

      let variantId;
      if (sizeSelect) {
        if (!sizeSelect.value) {
          showError(errorEl, 'Please select a size.');
          return;
        }
        variantId = parseInt(sizeSelect.value, 10);
      } else {
        variantId = parseInt(btn.dataset.variantId, 10);
      }

      if (!variantId) {
        showError(errorEl, 'This product is unavailable.');
        return;
      }

      hideError(errorEl);
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        const ok = await window.GRGClub.addToCart(variantId, 1, CLUB_TAG);
        if (!ok) throw new Error('Could not add to cart.');
        // GRGClub.addToCart already dispatches cart:update with sections + qty.
        // The flash overlays a ✓ Added pop on the button for 1.3s.
        if (window.GRGClub.flashAdded) window.GRGClub.flashAdded(btn);
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 1300);
      } catch (err) {
        showError(errorEl, err.message);
        btn.disabled = false;
        btn.textContent = originalText;
      }
    });
  });

  function resetModal(modal) {
    modal.querySelectorAll('.grg-age-select').forEach((s) => (s.value = ''));
    modal.querySelectorAll('.grg-bundle-age-select').forEach((s) => (s.value = s.dataset.lockedAge || ''));
    modal.querySelectorAll('.grg-size-select').forEach((s) => (s.value = ''));
    modal.querySelectorAll('.grg-bundle-component-size').forEach((s) => {
      s.value = '';
      filterSizesByAge(s, '');
    });
    modal.querySelectorAll('.grg-initials-input').forEach((i) => (i.value = ''));
    modal.querySelectorAll('.grg-bundle-confirm').forEach((c) => (c.checked = false));
    modal.querySelectorAll('.grg-size-field').forEach((f) => {
      if (f.previousElementSibling?.querySelector?.('.grg-age-select')) f.hidden = true;
    });
    // Re-apply the age filter/lock state to every bundle size dropdown.
    modal.querySelectorAll('.grg-bundle-age-select').forEach((s) => s.dispatchEvent(new Event('change')));
  }

  function showError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function hideError(el) {
    if (!el) return;
    el.hidden = true;
  }

  // Builds a readable plaintext summary for a pack add, for the order note.
  // properties may contain both component sizes (e.g. "Quarter Zip (Light)": "5-6")
  // and initials keys (e.g. "Initials 1": "ABC").
  function buildPackSummary(packTitle, ageVariant, properties) {
    const sizeLines = [];
    const initialsLines = [];
    Object.entries(properties).forEach(([key, val]) => {
      if (/^Initials \d+$/.test(key)) {
        initialsLines.push(`   • ${key}: ${val}`);
      } else {
        sizeLines.push(`   • ${key}: ${val}`);
      }
    });
    return [`▸ ${packTitle} (${ageVariant})`, ...sizeLines, ...initialsLines].join('\n');
  }

  // Appends a block of text to the cart note. Preserves anything already there
  // (delivery estimates, customer notes, prior pack summaries).
  async function appendCartNote(summary) {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      const existing = (cart.note || '').trim();
      const newNote = existing ? `${existing}\n\n${summary}` : summary;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: newNote }),
      });
    } catch (e) {
      // Note is a nice-to-have — never block checkout if this fails
      console.warn('Failed to append cart note:', e);
    }
  }

  // ---- Filter: auto-submit on checkbox change ----
  document.querySelectorAll('.grg-filter-checkbox').forEach((cb) => {
    cb.addEventListener('change', () => cb.closest('form').submit());
  });

  // ---- Filter: close dropdown on outside click ----
  document.addEventListener('click', (e) => {
    document.querySelectorAll('.grg-filter-dropdown[open]').forEach((dd) => {
      if (!dd.contains(e.target)) dd.removeAttribute('open');
    });
  });

  // ---- Grid density toggle ----
  const grid = document.getElementById('grg-items-grid');
  const densityBtns = document.querySelectorAll('.grg-density-btn');

  function setDensity(density) {
    if (grid) grid.dataset.density = density;
    densityBtns.forEach((b) => {
      b.classList.toggle('grg-density-btn--active', b.dataset.density === density);
    });
    try { localStorage.setItem('grg-portal-density', density); } catch (e) {}
  }

  densityBtns.forEach((btn) => {
    btn.addEventListener('click', () => setDensity(btn.dataset.density));
  });

  // Restore saved density preference
  try {
    const saved = localStorage.getItem('grg-portal-density');
    if (saved === 'grid' || saved === 'list') setDensity(saved);
  } catch (e) {}
})();
